import { ChangeDetectorRef, Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { OneWebComponentsAngularModule } from '@one/angular';
import { Observable, take } from 'rxjs';
import { FileUploadService } from '../../file-upload/file-upload.service';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ImportDiaologTableService } from '../import-dialog-table/import-dialog-table.service';
import { FileUploadComponent } from '../../file-upload/file-upload.component';
import { ImportDialogTableComponent } from '../import-dialog-table/import-dialog-table.component';

export interface DialogData {
  fileData: string[];
  columnData: any;
  sourceName: string;
}

export interface ColumnIssue {
  columnName: string;
  /** true when the column header is completely absent from the uploaded file */
  missingHeader: boolean;
  /** 1-based row numbers (row 1 = header) that contain an empty/blank value */
  emptyRows: number[];
}

@Component({
  selector: 'dl-import-dialog-file',
  standalone: true,
  imports: [OneWebComponentsAngularModule, FileUploadComponent, ImportDialogTableComponent, ReactiveFormsModule, FormsModule],
  templateUrl: './import-dialog-file.component.html',
  styleUrl: './import-dialog-file.component.scss',
  providers: [FileUploadService, ImportDiaologTableService]
})
export class ImportDialogFileComponent {

  public filename = new FormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(25), Validators.pattern('[a-zA-Z ]*')]);

  public importButton = true;
  public getImportData: Observable<string>;
  public checkdropdownFlag: string;
  @Input() inputData: any;
  public data: any = {};
  @Output() dialogClose = new EventEmitter<any>();
  /** Emits per-column issues (missing header or blank rows) right after file upload */
  @Output() blankColumnsDetected = new EventEmitter<ColumnIssue[]>();
  public fileData: any;
  public columnData: any;
  public sourceName: string;

  /** Keeps Import disabled as long as the file has empty/missing data */
  public hasBlankColumns = false;

  public constructor(
    private fileuploadService: FileUploadService,
    private importDiaologTableService: ImportDiaologTableService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  public onImportbtnCheckData(): Observable<string> {
    return this.fileuploadService.checkValidDataobservable$;
  }

  public ngOnInit(): void {
    this.getImportData = this.onImportbtnCheckData();

    this.getImportData.subscribe(d => {
      this.checkdropdownFlag = d;
      this.updateImportButtonState();
      this.changeDetectorRef.detectChanges();
    });

    this.filename.valueChanges.subscribe(() => {
      if (!this.filename.valid) {
        this.filename.markAsDirty();
      }
      this.updateImportButtonState();
      this.changeDetectorRef.detectChanges();
    });

    // Track latest column mapping for onImport()
    this.importDiaologTableService.mappingData$.subscribe(impData => {
      this.columnData = impData;
    });
  }

  /**
   * Scans every column that exists in the uploaded file using the actual column
   * headers. For each column that has at least one empty/blank cell, it returns
   * the real column name and the 1-based row numbers (row 1 = header row) that
   * are empty. Works for any file regardless of column naming.
   */
  private detectIssues(fileColumnHeaders: string[], fileData: any[]): ColumnIssue[] {
    const issues: ColumnIssue[] = [];
    if (!fileData || fileData.length === 0 || !fileColumnHeaders?.length) return issues;

    for (const header of fileColumnHeaders) {
      const emptyRows: number[] = [];
      fileData.forEach((row: any, index: number) => {
        const val = row[header];
        if (val === undefined || val === null || String(val).trim() === '') {
          emptyRows.push(index + 2); // row 1 = header; data rows start at 2
        }
      });
      if (emptyRows.length > 0) {
        issues.push({ columnName: header, missingHeader: false, emptyRows });
      }
    }

    return issues;
  }

  /** Disables Import when dropdowns are incomplete, filename is invalid, or blank data exists */
  private updateImportButtonState(): void {
    if (
      this.checkdropdownFlag === 'Import button Active' &&
      this.filename.valid &&
      !this.hasBlankColumns
    ) {
      this.importButton = false;
      this.data.sourceName = this.filename.value;
    } else {
      this.importButton = true;
    }
  }

  public onFileUpload(evt: any): void {
    if (evt !== undefined) {
      // File deleted — reset everything
      if (evt.action === 'delete') {
        this.fileData = null;
        this.hasBlankColumns = false;
        this.updateImportButtonState();
        this.changeDetectorRef.detectChanges();
        return;
      }

      let filenameOnupload, filenameOnuploadValid: string;
      if (evt.fileDetails.name) {
        filenameOnupload = evt.fileDetails.name.split('.')[0];
        filenameOnuploadValid = filenameOnupload.replace(/[^a-zA-Z ]/g, '');
        if (filenameOnuploadValid.length > 25) {
          filenameOnuploadValid = filenameOnuploadValid.slice(0, 25);
        }
      }
      evt.fileDetails.name ? this.filename.setValue(filenameOnuploadValid) : this.filename.setValue('');

      // BehaviorSubject already holds the latest parsed data — fires synchronously
      this.fileuploadService.sheetToJsonDataobservable$.pipe(take(1)).subscribe(d => {
        this.fileData = d;
      });

      // Immediately scan every column in the file for empty cells
      const issues = this.detectIssues(evt.columnDetails || [], this.fileData);

      if (issues.length > 0) {
        this.hasBlankColumns = true;
        this.blankColumnsDetected.emit(issues); // parent opens the warning modal
      } else {
        this.hasBlankColumns = false;
      }

      this.updateImportButtonState();
      this.changeDetectorRef.detectChanges();
      this.sourceName = this.filename.value;
    }
  }

  onImport() {
    const dataToReturn = {
      fileData: this.fileData,
      columnData: this.columnData,
      sourceName: this.filename.value || this.sourceName
    };
    this.dialogClose.emit(dataToReturn);
  }

  onCancel() {
    this.dialogClose.emit(null);
  }
}
